import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned action cluster (typically the primary "+ New …" button). */
  actions?: React.ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
  /**
   * Slot rendered above the title — usually a `<Breadcrumb>` or a small
   * inline trail like `Quotes / #1234`. Stays muted-foreground sized so it
   * doesn't compete with the title.
   */
  breadcrumb?: React.ReactNode;
  className?: string;
}

/**
 * PageHeader
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.2 (list pages) +
 * §3.1 (detail pages).
 *
 * Rules baked in:
 *   - Title is `text-3xl font-semibold` exactly. Never raise/lower this size
 *     per page — settings sub-pages that need a smaller heading roll their
 *     own `<h1>` outside `<PageHeader>` (see InboundEmail / Notifications).
 *   - The right cluster contains ONLY the primary action on list pages
 *     ("+ New Foo"). Filters/search/columns live in `<HeroToolbar>` below.
 *   - On detail pages the right cluster may carry an action row (Edit /
 *     Download PDF / ⋮ menu). Spec dictates each is `size="sm"`.
 *   - Breadcrumb sits above the title in `text-xs text-muted-foreground`
 *     when supplied.
 *
 * `data-testid="text-page-title"` is fixed so smoke tests can find the
 * page heading regardless of which page they're on.
 */
export function PageHeader({
  title,
  description,
  actions,
  showBackButton = false,
  onBack,
  breadcrumb,
  className,
}: PageHeaderProps) {
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {breadcrumb ? (
        <div className="text-xs text-muted-foreground" data-testid="breadcrumb-slot">
          {breadcrumb}
        </div>
      ) : null}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {showBackButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="shrink-0"
              data-testid="button-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1
              className="text-3xl font-semibold leading-tight"
              data-testid="text-page-title"
            >
              {title}
            </h1>
            {description && (
              <p className="text-muted-foreground text-sm mt-1">
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap">{actions}</div>
        )}
      </div>
    </div>
  );
}
